import { useState, useCallback } from 'react';
import { useProjectStore, Project, Schedule } from '../contexts/projectStoreProvider';
import { getProjects, createProject as createProjectApi, updateProject, deleteProject } from '../api/projectApi';

export const useProjects = () => {
  const { 
    projects, 
    setProjects, 
    currentProject, 
    setCurrentProject,
    organizations,
    addProject: addProjectToStore
  } = useProjectStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch projects (in a real app, this would call an API)
  const fetchProjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // In a real app, this would be an API call
      // const response = await fetch('/api/projects');
      // const data = await response.json();
      // setProjects(data);
      
      // Using mock data from the store for now
      setIsLoading(false);
      return projects;
    } catch (err) {
      setError('Failed to fetch projects');
      setIsLoading(false);
      return [];
    }
  }, [projects]);

  // Create a new project
  const createProject = useCallback(async (projectData: Omit<Project, 'id' | 'created_at' | 'updated_at'>) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // In a real app, this would be an API call
      // const response = await fetch('/api/projects', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(projectData)
      // });
      // const data = await response.json();
      
      // Mock creating a project
      const newProject: Project = {
        ...projectData,
        id: `new-${Date.now()}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        schedules: []
      };
      
      addProjectToStore(newProject);
      setIsLoading(false);
      return newProject;
    } catch (err) {
      setError('Failed to create project');
      setIsLoading(false);
      return null;
    }
  }, [addProjectToStore]);

  // Update an existing project
  const updateProject = useCallback(async (projectId: string, projectData: Partial<Project>) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // In a real app, this would be an API call
      // const response = await fetch(`/api/projects/${projectId}`, {
      //   method: 'PUT',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(projectData)
      // });
      // const data = await response.json();
      
      // Mock updating a project
      const updatedProjects = projects.map(project => 
        project.id === projectId 
          ? { 
              ...project, 
              ...projectData, 
              updated_at: new Date().toISOString() 
            } 
          : project
      );
      
      setProjects(updatedProjects);
      
      // If the current project is being updated, update it as well
      if (currentProject && currentProject.id === projectId) {
        setCurrentProject({
          ...currentProject,
          ...projectData,
          updated_at: new Date().toISOString()
        });
      }
      
      setIsLoading(false);
      return updatedProjects.find(p => p.id === projectId) || null;
    } catch (err) {
      setError('Failed to update project');
      setIsLoading(false);
      return null;
    }
  }, [projects, setProjects, currentProject, setCurrentProject]);

  // Delete a project
  const deleteProject = useCallback(async (projectId: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // In a real app, this would be an API call
      // await fetch(`/api/projects/${projectId}`, {
      //   method: 'DELETE'
      // });
      
      // Mock deleting a project
      const updatedProjects = projects.filter(project => project.id !== projectId);
      setProjects(updatedProjects);
      
      // If the current project is being deleted, clear it
      if (currentProject && currentProject.id === projectId) {
        setCurrentProject(null);
      }
      
      setIsLoading(false);
      return true;
    } catch (err) {
      setError('Failed to delete project');
      setIsLoading(false);
      return false;
    }
  }, [projects, setProjects, currentProject, setCurrentProject]);

  // Add a schedule to a project
  const addScheduleToProject = useCallback(async (projectId: string, scheduleData: Omit<Schedule, 'id' | 'created_at'>) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // In a real app, this would be an API call
      // const response = await fetch(`/api/projects/${projectId}/schedules`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(scheduleData)
      // });
      // const data = await response.json();
      
      // Mock adding a schedule
      const newSchedule: Schedule = {
        ...scheduleData,
        id: `schedule-${Date.now()}`,
        created_at: new Date().toISOString()
      };
      
      const updatedProjects = projects.map(project => {
        if (project.id === projectId) {
          return {
            ...project,
            schedules: [...(project.schedules || []), newSchedule],
            updated_at: new Date().toISOString()
          };
        }
        return project;
      });
      
      setProjects(updatedProjects);
      
      // If the current project is being updated, update it as well
      if (currentProject && currentProject.id === projectId) {
        setCurrentProject({
          ...currentProject,
          schedules: [...(currentProject.schedules || []), newSchedule],
          updated_at: new Date().toISOString()
        });
      }
      
      setIsLoading(false);
      return newSchedule;
    } catch (err) {
      setError('Failed to add schedule');
      setIsLoading(false);
      return null;
    }
  }, [projects, setProjects, currentProject, setCurrentProject]);

  // Get projects by organization
  const getProjectsByOrganization = useCallback(async (organizationId: string): Promise<Project[]> => {
    try {
      setIsLoading(true);
      setError(null);
      const projects = await getProjects();
      return projects.filter(project => project.organization === organizationId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch projects');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    projects,
    currentProject,
    setCurrentProject,
    isLoading,
    error,
    fetchProjects,
    createProject,
    updateProject,
    deleteProject,
    addScheduleToProject,
    getProjectsByOrganization
  };
}; 